import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { compact } from 'lodash-es';
import { NgxMaskPipe } from 'ngx-mask';
import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { LimitItem, LimitUpdateRequest } from '@shared/models';
import { ErrorUtils, LimitAccountType } from '@shared/utils';
import { CurrencySignPipe, PrefixPipe } from '@shared/pipes';
import { ExpandableFormSectionComponent, AddressEditComponent } from '@shared/components';
import { LimitEditItemFieldComponent } from '../limit-edit-item-field/limit-edit-item-field.component';
import { BusinessAccountService, FinancialAccountService, NotificationService } from '@shared/services';
import { finalize, Subject, takeUntil } from 'rxjs';

@Component({
  templateUrl: './update-transaction-limits.component.html',
  standalone: true,
  imports: [
    CommonModule,
    RktIconComponent,
    RktButtonDirective,
    AddressEditComponent,
    ExpandableFormSectionComponent,
    LimitEditItemFieldComponent,
    NgxMaskPipe,
    PrefixPipe,
    CurrencySignPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpdateTransactionLimitsComponent {
  businessAccountService = inject(BusinessAccountService);
  financialAccountService = inject(FinancialAccountService);
  notificationService = inject(NotificationService);
  activeModal = inject(ActiveModal);

  limitAccountId!: string;

  limitAccountType?: LimitAccountType;

  transactionLimits: LimitItem[] = [];

  isLoading = false;

  submitError = signal('');

  currency!: LimitItem['currency'];

  values: Record<string, number> = {
    single: 0,
    daily: 0,
    weekly: 0,
    monthly: 0,
  };

  maxLimits: Record<string, number> = {
    single: 0,
    daily: 0,
    weekly: 0,
    monthly: 999999999,
  };

  valueErrors: Record<string, boolean> = {
    single: false,
    daily: false,
    weekly: false,
    monthly: false,
  };

  get isLimitsHasError(): boolean {
    return compact(this.transactionLimits.map((item) => this.valueErrors[item.limitType.toLowerCase()])).length > 0;
  }

  get isLimitsModified(): boolean {
    return compact(this.transactionLimits.map((item) => this.values[item.limitType.toLowerCase()] !== Number(item.amount))).length > 0;
  }

  private destroy$ = new Subject<void>();

  modalInitData({
    limitAccountId,
    limitAccountType,
    transactionLimits,
    currency,
  }: {
    limitAccountId: string;
    limitAccountType: LimitAccountType;
    transactionLimits: LimitItem[];
    currency: string;
  }): void {
    this.limitAccountId = limitAccountId;
    this.limitAccountType = limitAccountType;
    this.transactionLimits = [...transactionLimits];
    this.currency = currency;

    this.transactionLimits.forEach((item) => {
      this.values[item.limitType.toLowerCase()] = Number(item.amount);
    });

    this.mapMaxLimits();

    this.valueErrors = {
      single: false,
      daily: false,
      weekly: false,
      monthly: false,
    };
  }

  onSave() {
    const limits: LimitUpdateRequest = this.transactionLimits
      .filter((item) => this.values[item.limitType.toLowerCase()] !== item.amount)
      .reduce((obj, limitItem) => {
        return { ...obj, [limitItem.limitType]: { amount: this.values[limitItem.limitType.toLowerCase()] } };
      }, {} as LimitUpdateRequest);

    if (!limits) {
      return;
    }

    this.isLoading = true;

    if (this.limitAccountType === LimitAccountType.BUSINESS_ACCOUNT) {
      this.updateBusinessAccountLimits(limits);
    } else {
      this.updateFinancialAccountLimits(limits);
    }
  }

  updateBusinessAccountLimits(limits: LimitUpdateRequest): void {
    this.businessAccountService
      .updateTransactionLimits({
        businessAccountId: this.limitAccountId,
        limits,
        currency: this.currency,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: () => this.onUpdateLimitsSuccess(),
        error: (error) => this.onUpdateLimitsFailed(error),
      });
  }

  updateFinancialAccountLimits(limits: LimitUpdateRequest): void {
    this.financialAccountService
      .updateFinancialAccountLimits(this.limitAccountId, limits)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: () => this.onUpdateLimitsSuccess(),
        error: (error) => this.onUpdateLimitsFailed(error),
      });
  }

  onUpdateLimitsSuccess(): void {
    this.activeModal.close(true);
    this.notificationService.displaySuccess('Transaction Limits updated successfully.');
  }

  onUpdateLimitsFailed(error: unknown): void {
    const errorMessage = ErrorUtils.parseExtendedError(error);
    this.submitError.set(errorMessage);
    ErrorUtils.catchError('limitsService.updateTransactionLimits', errorMessage);
  }

  setLimitAmountValue(value: string, type: string): void {
    if (value == null) return;

    if (this.submitError()) {
      this.submitError.set('');
    }
    this.values[type.toLowerCase()] = parseFloat(value);
    this.mapMaxLimits();
  }

  onLimitAmountValueError(isError: boolean, type: string): void {
    this.valueErrors[type] = isError;
  }

  mapMaxLimits(): void {
    Object.keys(this.values).forEach((type) => {
      switch (type) {
        case 'daily':
          this.maxLimits['single'] = this.values[type];
          break;
        case 'weekly':
          this.maxLimits['daily'] = this.values[type];
          break;
        case 'monthly':
          this.maxLimits['weekly'] = this.values[type];
          break;
        default:
          break;
      }
    });
  }
}
